import React from 'react';
import { Container, Stack, Title, Text, Group, TextInput, Button, Card, Grid, Badge, ActionIcon } from '@mantine/core';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { PLAN_TYPES } from '../services/billing/constants';
import { IconPencil, IconX, IconCheck } from '@tabler/icons-react';
import { PlanService } from '../services/billing/PlanService';

export default function Account() {
  const user = auth.currentUser;
  const [displayName, setDisplayName] = React.useState(user?.displayName || '');
  const [saving, setSaving] = React.useState(false);
  const [editingName, setEditingName] = React.useState(false);
  const [currentPlan, setCurrentPlan] = React.useState('FREE');
  const [loadingPlan, setLoadingPlan] = React.useState(true);
  const [message, setMessage] = React.useState('');
  const [scheduled, setScheduled] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPlan(true);
        const plan = await PlanService.getCurrentPlan(user?.uid);
        if (!cancelled) setCurrentPlan(plan || 'FREE');
        const sched = await PlanService.listScheduledPlans(user?.uid);
        if (!cancelled) setScheduled(sched);
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { displayName: displayName || null });
      setMessage('Profile updated');
      setEditingName(false);
    } catch (e) {
      setMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const upgradePlan = async (type) => {
    try {
      if (type === PLAN_TYPES.FREE) {
        // Downgrade to FREE -> schedule next cycle
        await PlanService.schedulePlanChange(user.uid, type);
        setMessage(`Plan change to ${type} scheduled for next billing cycle`);
      } else if (currentPlan === PLAN_TYPES.FREE) {
        // FREE -> paid: immediate via Stripe Checkout
        const idToken = await user.getIdToken();
        const res = await fetch('/.netlify/functions/stripe-create-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ planType: type }),
        });
        const data = await res.json();
        if (!res.ok || !data?.url) throw new Error(data?.message || data?.error || 'checkout_failed');
        window.location.href = data.url;
        return;
      } else {
        // Paid -> paid: schedule change for next cycle via Stripe Subscription Schedule
        const idToken = await user.getIdToken();
        const res = await fetch('/.netlify/functions/stripe-schedule-plan-change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ planType: type }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || data?.error || 'schedule_failed');
        setMessage(`Plan change to ${type} scheduled for next billing cycle`);
      }
      const sched = await PlanService.listScheduledPlans(user?.uid);
      setScheduled(sched);
    } catch (e) {
      const apiMsg = e?.message ? `: ${e.message}` : '';
      if (type === PLAN_TYPES.FREE) {
        setMessage(`Failed to cancel subscription${apiMsg}`);
      } else if (currentPlan === PLAN_TYPES.FREE) {
        setMessage(`Checkout failed${apiMsg}`);
      } else {
        setMessage(`Failed to schedule plan change${apiMsg}`);
      }
      console.error('upgradePlan error', e);
    }
  };

  const PlanCard = ({ name, price, period, features, popular, planType, cta }) => (
    <Card withBorder radius="md" p="lg" style={{ height: '100%' }}>
      {popular && <Badge color="blue" mb="sm">Popular</Badge>}
      <Stack style={{ height: '100%' }} justify="space-between">
        <Stack gap={6}>
          <Text fw={600}>{name}</Text>
          <Group gap={6}>
            <Title order={2}>{price}</Title>
            <Text c="dimmed">{period}</Text>
          </Group>
          <Stack gap={4}>
            {features.map((f, i) => (
              <Text key={i} c="dimmed">• {f}</Text>
            ))}
          </Stack>
        </Stack>
        <Button variant={popular ? 'filled' : 'outline'} onClick={() => upgradePlan(planType)} disabled={loadingPlan || currentPlan === planType}>
          {currentPlan === planType ? 'Current' : cta}
        </Button>
      </Stack>
    </Card>
  );

  return (
    <Container>
      <Stack gap="md">
        <Title order={2}>My Account</Title>
        {message && <Text c="dimmed">{message}</Text>}

        <Card withBorder radius="md" p="lg">
          <Title order={4} mb="sm">Profile</Title>
          {editingName ? (
            <Group align="flex-end" gap="sm">
              <TextInput label="Display name" value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} w={300} />
              <Button onClick={saveProfile} loading={saving} leftSection={<IconCheck size={16} />}>Save</Button>
              <Button variant="default" onClick={() => { setEditingName(false); setDisplayName(user?.displayName || ''); }} leftSection={<IconX size={16} />}>Cancel</Button>
            </Group>
          ) : (
            <Group align="center" gap="sm">
              <Text fw={600}>Display name:</Text>
              <Text>{displayName || '—'}</Text>
              <ActionIcon variant="subtle" onClick={() => setEditingName(true)} title="Edit display name">
                <IconPencil size={18} />
              </ActionIcon>
            </Group>
          )}
        </Card>

        <Card withBorder radius="md" p="lg">
          <Title order={4} mb="sm">Plan</Title>
          <Text c="dimmed" mb="md">Current plan: {loadingPlan ? '...' : currentPlan}</Text>
          {scheduled.length > 0 && (
            <Stack mb="md" gap={6}>
              <Text fw={600}>Scheduled changes</Text>
              {scheduled.map((s) => (
                <Group key={s.id} justify="space-between">
                  <Text>
                    {s.type} starting {s.effectiveDate?.toDate ? s.effectiveDate.toDate().toLocaleDateString() : ''}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
          <Grid gutter="md" align="stretch">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <PlanCard
                name="Free"
                price="0€"
                period="/month"
                features={[
                  '1 calendar',
                  'Up to 3 people',
                  'Simple constraint set',
                  '.ics calendar export',
                  'Weekly optimization',
                ]}
                popular={false}
                planType={PLAN_TYPES.FREE}
                cta="Start for free"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <PlanCard
                name="Basic"
                price="9.99€"
                period="/month"
                features={[
                  'Unlimited calendars',
                  'Up to 10 member per calendar',
                  'Simple constraints',
                  '.ics export',
                  'Monthly optimization',
                ]}
                popular={false}
                planType={PLAN_TYPES.BASIC}
                cta="Try Basic"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <PlanCard
                name="Pro"
                price="99.90€"
                period="/month"
                features={[
                  'Unlimited calendars',
                  'Unlimited member per calendar',
                  'Email support',
                  'Constraint customization at onboarding',
                ]}
                popular={false}
                planType={PLAN_TYPES.PRO}
                cta="Choose Pro"
              />
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}
